import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { HBH_EPISODES } from '../../data/hbhEpisodes';
import { HBH_EPISODE_COUNT } from '../../data/siteStats';

/**
 * FeaturedOnStrip - "Watch and Learn" video-library teaser.
 *
 * Presents the embedded video library as a first-class BuildPilot surface:
 * a video thumbnail (latest episode still) with a play affordance and the
 * BuildPilot logo chip, next to a "Watch and Learn" heading and a CTA into
 * the on-site /watch library. Deliberately brand-neutral - no third-party
 * naming - so it reads as part of the BuildPilot experience.
 */
const FeaturedOnStrip = () => {
  const featured = (HBH_EPISODES && HBH_EPISODES[0]) || {};
  const thumb = featured.youtubeId
    ? `https://i.ytimg.com/vi/${featured.youtubeId}/hqdefault.jpg`
    : featured.thumbnail;

  return (
    <section className="py-10 sm:py-14 bg-[#fafbfc] border-y border-gray-200" data-testid="featured-on-strip">
      <div className="max-w-[1080px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
          {/* Video thumbnail - the whole card opens the on-site library */}
          <Link
            to="/watch"
            className="group relative shrink-0 w-full sm:w-[320px] aspect-video rounded-2xl overflow-hidden ring-1 ring-black/10 shadow-[0_12px_32px_-14px_rgba(0,0,0,0.45)]"
            data-testid="featured-on-video"
            aria-label="Watch and learn - browse the video library"
          >
            {thumb && (
              <img
                src={thumb}
                alt=""
                loading="lazy"
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              />
            )}
            <span className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" aria-hidden="true" />
            {/* Play affordance */}
            <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
              <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play className="w-6 h-6 text-[#1d1d1f] ml-0.5" fill="#1d1d1f" />
              </span>
            </span>
            {/* BuildPilot logo chip - frames the library as our own */}
            <span className="absolute bottom-2.5 left-2.5 bg-white rounded-md px-2 py-1 shadow-sm" aria-hidden="true">
              <img src="/images/buildpilot-logo-2026-dark.png" alt="BuildPilot" className="h-3.5 w-auto" />
            </span>
          </Link>

          <div className="min-w-0 flex-1 text-center sm:text-left">
            <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-[-0.02em] leading-[1.15] mb-2">
              Watch and Learn
            </h2>
            <p className="text-[15px] text-[#5a5a5f] leading-[1.55] max-w-[560px] mx-auto sm:mx-0 mb-5">
              Real builders, brokers and buyers break down the new-home process in their own words. Browse{' '}
              <span className="font-semibold text-[#1d1d1f] tabular-nums">{HBH_EPISODE_COUNT} videos</span>{' '}
              woven through our guides.
            </p>
            <Link
              to="/watch"
              className="inline-flex items-center justify-center gap-1.5 bg-[#1d1d1f] hover:bg-black text-white px-5 py-3 rounded-full text-[13.5px] font-semibold transition-colors"
              data-testid="featured-on-watch-cta"
            >
              Browse the video library
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedOnStrip;
